import { useState } from "react";
import { Link, NavLink as RouterNavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { SidebarTrigger } from "@/components/ui/sidebar";
import pdfWorkSpaceLogo from "@/assets/logo.png";
import { useSupporter } from "@/context/SupporterContext";
import { ProSupporterDialog } from "@/components/ProSupporterDialog";
import { Coffee, ShieldCheck } from "lucide-react";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/tools", label: "Tools" },
  { to: "/batch-tools", label: "Batch Tools" },
  { to: "/p2p-tools", label: "P2P Tools" },
  // { to: "/blog", label: "Blog" },
  { to: "/faq", label: "FAQ" },
  { to: "/about-privacy", label: "About" },
];

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [supportDialogOpen, setSupportDialogOpen] = useState(false);
  const location = useLocation();
  const path = location.pathname;
  const showSidebarTrigger = path.startsWith("/tools") || path.startsWith("/p2p-tools") || path.startsWith("/batch-tools");
  const { isProSupporter } = useSupporter();

  return (
    <>
    <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur-md">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3">
        {/* Left: Logo & Sidebar */}
        <div className="flex items-center gap-3">
          {showSidebarTrigger && (
            <SidebarTrigger className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border text-text-secondary transition-all hover:bg-secondary hover:text-foreground" />
          )}
          <Link to="/" className="group flex items-center gap-3">
            <div className="relative">
              <img
                src={pdfWorkSpaceLogo}
                alt="PDFWorkSpace"
                className="h-10 w-10 transition-transform duration-200 group-hover:scale-105"
              />
            </div>
            <div className="flex flex-col">
              <span className="font-serifAccent text-xl tracking-tight text-foreground">
                PDFWorkSpace
              </span>
              <span className="hidden text-[0.6rem] uppercase tracking-widest text-text-secondary/70 sm:block">
                Local PDF Tools
              </span>
            </div>
          </Link>
        </div>

        {/* Center: Desktop Navigation */}
        <nav className="hidden items-center gap-1 md:flex">
          {navLinks.map((link) => (
            <RouterNavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? "text-foreground"
                    : "text-text-secondary hover:text-foreground"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {link.label}
                  {isActive && (
                    <span className="absolute bottom-0 left-1/2 h-0.5 w-6 -translate-x-1/2 rounded-full bg-foreground" />
                  )}
                </>
              )}
            </RouterNavLink>
          ))}
        </nav>

        {/* Right: CTA & Mobile Menu */}
        <div className="flex items-center gap-3">
          {/* Pro Supporter / Privacy Badge - Desktop */}
          <div className="hidden lg:flex items-center gap-2">
            {isProSupporter ? (
              <button 
                onClick={() => setSupportDialogOpen(true)}
                className="flex items-center gap-1.5 rounded-full border border-amber-500/20 bg-amber-500/10 px-3 py-1.5 transition-colors hover:bg-amber-500/20"
              >
                <ShieldCheck className="h-4 w-4 text-amber-500" />
                <span className="text-xs font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400">
                  Pro Supporter
                </span>
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSupportDialogOpen(true)}
                  className="group flex items-center gap-1.5 rounded-full border border-border bg-secondary px-3 py-1.5 transition-all hover:border-amber-500/30 hover:bg-amber-500/5 hover:text-amber-600"
                >
                  <Coffee className="h-3.5 w-3.5 text-amber-500 group-hover:scale-110 transition-transform" />
                  <span className="text-xs font-medium text-text-secondary group-hover:text-amber-600 dark:group-hover:text-amber-400">
                    Support Us
                  </span>
                </button>
                <div className="flex items-center gap-1.5 rounded-full border border-green-500/20 bg-green-500/5 px-3 py-1.5">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-75"></span>
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-green-500"></span>
                  </span>
                  <span className="text-xs font-medium text-green-600 dark:text-green-400">
                    Private & Local
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Get Started Button - Desktop */}
          <Link
            to="/tools"
            className="group hidden items-center gap-2 rounded-full bg-foreground px-5 py-2 text-sm font-medium text-background transition-all hover:bg-foreground/90 md:inline-flex"
          >
            Get Started
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>

          {/* Mobile Menu Button */}
          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenuOpen}
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border text-foreground transition-colors hover:bg-secondary md:hidden"
          >
            {mobileMenuOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="border-t border-border bg-background px-4 py-4 md:hidden">
          <nav className="flex flex-col gap-1">
            {navLinks.map((link) => (
              <RouterNavLink
                key={link.to}
                to={link.to}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `rounded-lg px-4 py-3 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-secondary text-foreground"
                      : "text-text-secondary hover:bg-secondary hover:text-foreground"
                  }`
                }
              >
                {link.label}
              </RouterNavLink>
            ))}
            <Link
              to="/tools"
              onClick={() => setMobileMenuOpen(false)}
              className="mt-2 flex items-center justify-center gap-2 rounded-full bg-foreground px-5 py-3 text-sm font-medium text-background"
            >
              Get Started
              <ArrowRight className="h-4 w-4" />
            </Link>
          </nav>
        </div>
      )}
    </header>
    <ProSupporterDialog isOpen={supportDialogOpen} onClose={() => setSupportDialogOpen(false)} />
    </>
  );
};
